import { Component, For, Show } from "solid-js";
import { createDroppable, SortableProvider } from "@thisbeyond/solid-dnd";
import { WorkItemCard } from "./WorkItemCard";
import type { ColumnWithStatuses, WorkItem, WorkItemType } from "../../api";

export interface BoardColumnProps {
  column: ColumnWithStatuses;
  items: WorkItem[];
  itemTypes?: WorkItemType[];
  childCounts?: Record<string, number>;
  parentTitles?: Record<string, string>;
  isDropTarget?: boolean;
  /** Slot where a cross-column drop would land. 0 = above the first card,
   *  items.length = below the last. null = no indicator. */
  dropIndex?: number | null;
  /** ID of the item whose context menu is currently open, for aria-expanded on its trigger. */
  openMenuItemId?: string;
  onItemClick?: (item: WorkItem) => void;
  onItemContextMenu?: (
    item: WorkItem,
    x: number,
    y: number,
    trigger: HTMLElement | null,
  ) => void;
  onAddItem?: (columnId: string, statusId: string) => void;
  /** Insert a card at a slot in this column. index 0 = above the first card. */
  onAddItemAt?: (columnId: string, statusId: string, index: number) => void;
  /** True while any card is being dragged — suppresses the insert affordances. */
  dragging?: boolean;
}

/**
 * BoardColumn - A single column in the Kanban board
 *
 * Features:
 * - Column header with name, item count, WIP indicator
 * - Vertical list of work item cards
 * - Drop target for drag-and-drop
 * - "Add item" button at bottom
 * - WIP limit visual warning when exceeded
 * - Empty state for columns with no items
 */
export const BoardColumn: Component<BoardColumnProps> = (props) => {
  const droppable = createDroppable(props.column.id);

  const itemCount = () => props.items.length;
  const hasWipLimit = () =>
    props.column.wip_limit !== null && props.column.wip_limit !== undefined;
  const isOverWipLimit = () =>
    hasWipLimit() && itemCount() >= (props.column.wip_limit ?? 0);

  // Get primary status_id for new items (first status in the column)
  const primaryStatusId = () => props.column.status_ids[0];

  const getItemType = (item: WorkItem) => {
    return props.itemTypes?.find((t) => t.id === item.item_type_id);
  };

  const handleAddClick = () => {
    const statusId = primaryStatusId();
    if (statusId) {
      props.onAddItem?.(props.column.id, statusId);
    }
  };

  // Top rule color keyed by column type — the status-hue accent replacing the
  // old tinted-header treatment.
  const topRule: Record<string, string> = {
    backlog: "border-t-line-strong",
    active: "border-t-signal-blue",
    done: "border-t-signal-green",
    archived: "border-t-ink-3",
  };

  return (
    <div
      data-board-column={props.column.id}
      class={`h-full flex flex-col bg-sunken rounded border border-t-2 flex-1 min-w-[220px] max-w-[360px] flex-shrink-0 transition-colors duration-150 ${props.isDropTarget ? "border-ink" : "border-line"} ${topRule[props.column.column_type] ?? topRule.active}`}
    >
      {/* Column Header */}
      <div class="flex items-center justify-between px-3 py-2 border-b border-line">
        <div class="flex items-center gap-2 min-w-0">
          <h3 class="font-semibold text-ink text-sm truncate">
            {props.column.name}
          </h3>
          <span
            class={
              "inline-flex items-center h-6 font-mono text-2xs tabular-nums px-1.5 rounded-sm border " +
              (isOverWipLimit()
                ? "bg-signal-amber-tint text-signal-amber border-signal-amber/25"
                : "bg-surface text-ink-2 border-line")
            }
            title={isOverWipLimit() ? "WIP limit reached" : undefined}
          >
            {itemCount()}
            <Show when={hasWipLimit()}>/{props.column.wip_limit}</Show>
            <Show when={isOverWipLimit()}>
              <span class="sr-only">WIP limit reached</span>
            </Show>
          </span>
        </div>

        {/* Quick add button in header */}
        <button
          type="button"
          class="p-1.25 text-ink-3 hover:text-ink hover:bg-surface rounded-sm transition-colors duration-150 focus-ring"
          title="Add item"
          onClick={handleAddClick}
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
        </button>
      </div>

      {/* Drop Zone / Card List with floating Add button */}
      <div
        ref={droppable.ref}
        data-board-drop-area
        class={`flex-1 overflow-y-auto p-2 min-h-[100px] transition-colors duration-150 ${droppable.isActiveDroppable || props.isDropTarget ? "bg-paper" : ""}`}
      >
        <SortableProvider ids={props.items.map((i) => i.id)}>
          <Show when={props.items.length === 0}>
            <div class="flex flex-col items-center justify-center py-8 px-4 pointer-events-none">
              <div class="text-line-strong mb-3">
                <svg
                  class="w-10 h-10"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <p class="text-sm text-ink-3 mb-4 text-center">No items yet</p>
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-ink bg-surface hover:bg-paper rounded border border-line transition-colors duration-150 flex items-center gap-1.5 focus-ring pointer-events-auto"
                onClick={handleAddClick}
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add first item
              </button>
            </div>
          </Show>

          <div class="space-y-4 pt-2">
            <For each={props.items}>
              {(item, i) => (
                <div class="relative group/insert">
                  <Show when={props.dropIndex === i()}>
                    <div
                      aria-hidden="true"
                      data-testid="drop-indicator"
                      class="h-0.5 rounded-full bg-ink my-1 pointer-events-none"
                    />
                  </Show>
                  <Show when={!props.dragging}>
                    <button
                      type="button"
                      aria-label={`Add item above ${item.title}`}
                      class="
                        group/divider absolute -top-4 inset-x-0 h-4 z-10 flex items-center
                        opacity-0 group-hover/insert:opacity-100 focus-visible:opacity-100
                        transition-opacity duration-100 focus-ring
                      "
                      onClick={() => {
                        const statusId = primaryStatusId();
                        if (statusId)
                          props.onAddItemAt?.(props.column.id, statusId, i());
                      }}
                    >
                      <span class="flex-1 border-t border-dashed border-line transition-colors duration-150 group-hover/divider:border-ink-3" />
                      <span
                        aria-hidden="true"
                        class="mx-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-line-strong text-[9px] leading-none text-paper transition-colors duration-150 group-hover/divider:bg-ink"
                      >
                        +
                      </span>
                      <span class="flex-1 border-t border-dashed border-line transition-colors duration-150 group-hover/divider:border-ink-3" />
                    </button>
                  </Show>
                  <WorkItemCard
                    item={item}
                    itemType={getItemType(item)}
                    childCount={props.childCounts?.[item.id]}
                    parentTitle={
                      item.parent_id
                        ? props.parentTitles?.[item.parent_id]
                        : undefined
                    }
                    menuOpen={props.openMenuItemId === item.id}
                    onClick={props.onItemClick}
                    onContextMenu={props.onItemContextMenu}
                  />
                </div>
              )}
            </For>
            <Show when={props.dropIndex === props.items.length}>
              <div
                aria-hidden="true"
                data-testid="drop-indicator"
                class="h-0.5 rounded-full bg-ink my-1 pointer-events-none"
              />
            </Show>
          </div>

          {/* Floating Add Item Button - appears after last card */}
          <Show when={props.items.length > 0}>
            <div class="mt-4">
              <button
                type="button"
                class="w-full py-2 px-2 text-sm text-ink-3 hover:text-ink hover:bg-surface rounded border border-dashed border-line hover:border-line-strong transition-colors duration-150 flex items-center justify-center gap-1.5 focus-ring"
                onClick={handleAddClick}
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add item
              </button>
            </div>
          </Show>
        </SortableProvider>
      </div>
    </div>
  );
};

export default BoardColumn;
